import React from "react";
import { MdBackup, MdCheckCircle, MdError, MdWarning, MdCloudUpload } from "react-icons/md";

const BackupStatsCards = ({ reportData, loading }) => {
  const parseBackupData = (backupDataStr) => {
    if (!backupDataStr) return [];
    try {
      const parsed = JSON.parse(backupDataStr);
      return parsed.filter(vm => vm.totalBackups !== null && vm.totalBackups > 0);
    } catch {
      return [];
    }
  };

  const backupData = parseBackupData(reportData?.backupData);

  const getSuccessRateColor = (rate) => {
    if (rate === 100) return "text-green-400";
    if (rate >= 80) return "text-yellow-400";
    return "text-red-400";
  };

  const getSuccessRateBadge = (rate) => {
    if (rate === 100) return "bg-green-500/20 text-green-400 border-green-500/30";
    if (rate >= 80) return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    return "bg-red-500/20 text-red-400 border-red-500/30";
  };

  const getProgressBarColor = (rate) => {
    if (rate === 100) return "bg-green-500";
    if (rate >= 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="mt-4 sm:mt-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
            <MdBackup className="text-primary-cyan text-xl sm:text-2xl" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">Backup Status</h3>
            <p className="text-gray-500 text-xs sm:text-sm">VM Backup Health Overview</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-primary-dark rounded-2xl p-4 sm:p-5 border border-gray-700/50 animate-pulse">
              <div className="h-32 sm:h-36"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (backupData.length === 0) {
    return (
      <div className="mt-4 sm:mt-6">
        <div className="bg-primary-dark rounded-2xl p-8 sm:p-12 text-center border border-gray-700/50">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <MdCloudUpload className="text-gray-600 text-3xl sm:text-4xl" />
          </div>
          <p className="text-gray-400 text-base sm:text-lg font-medium">No Backup Data Available</p>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">No VMs with backup records found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 sm:mt-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
        {backupData.map((vm, index) => (
          <div
            key={index}
            className="bg-primary-dark rounded-2xl p-4 sm:p-5 border border-gray-700/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary-cyan/20 hover:border-primary-cyan/30 group"
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-4">
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-bold text-base sm:text-lg truncate group-hover:text-primary-cyan transition-colors duration-200">
                  {vm.vmName}
                </h4>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Virtual Machine</p>
              </div>
              <div className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-bold whitespace-nowrap self-start ${getSuccessRateBadge(vm.successRate)}`}>
                {vm.successRate}% Success
              </div>
            </div>
            
            <div className="space-y-3 mt-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-400 text-[10px] sm:text-xs font-medium">Success Rate</span>
                  <span className={`text-xs sm:text-sm font-bold ${getSuccessRateColor(vm.successRate)}`}>
                    {vm.successRate}%
                  </span>
                </div>
                <div className="w-full h-1.5 sm:h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${getProgressBarColor(vm.successRate)}`}
                    style={{ width: `${vm.successRate}%` }}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <div>
                  <p className="text-gray-500 text-[10px] sm:text-xs">Total Backups</p>
                  <p className="text-white text-lg sm:text-xl font-bold">{vm.totalBackups}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-[10px] sm:text-xs">Failed Backups</p>
                  <p className="text-red-400 text-lg sm:text-xl font-bold">{vm.failedBackups || 0}</p>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-700/50">
                <div className="flex items-center gap-2">
                  {vm.successRate === 100 ? (
                    <>
                      <MdCheckCircle className="text-green-400 text-sm" />
                      <span className="text-green-400 text-[10px] sm:text-xs font-semibold">Fully Protected</span>
                    </>
                  ) : vm.successRate >= 80 ? (
                    <>
                      <MdWarning className="text-yellow-400 text-sm" />
                      <span className="text-yellow-400 text-[10px] sm:text-xs font-semibold">Partial Protection</span>
                    </>
                  ) : (
                    <>
                      <MdError className="text-red-400 text-sm" />
                      <span className="text-red-400 text-[10px] sm:text-xs font-semibold">Critical Risk</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BackupStatsCards;